import { Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { JsonPipe, NgForOf, NgIf } from "@angular/common";
import { CommonComponent } from "../../common/common-component";
import { ConfigService } from "../../common/service/config.service";
import { HttpClient } from "@angular/common/http";
import { BehaviorSubject, interval, map, merge, mergeMap, Subject, Subscription, takeUntil, tap, timer } from "rxjs";
import { environment } from "../../../environments/environment";
import { NotificationService } from "../../common/service/notification.service";
import { ContentBlockComponent } from "../../component/content-block/content-block.component";
import { parseJSON } from "date-fns";
import { FormatDistanceToNowPipeModule } from "ngx-date-fns";
import { Modal, ModalOptions } from "flowbite";
import { faPrint, faRefresh } from "@fortawesome/free-solid-svg-icons";
import { FaIconComponent } from "@fortawesome/angular-fontawesome";
import { TrackerService } from "../../common/service/tracker.service";
import Konva from "konva";
import { DefaultImageDirective } from "../../common/directive/default-image.directive";

@Component({
  selector: 'section#printable-gallery',
  standalone: true,
  imports: [
    NgIf,
    JsonPipe,
    NgForOf,
    ContentBlockComponent,
    FormatDistanceToNowPipeModule,
    FaIconComponent,
    DefaultImageDirective
  ],
  templateUrl: './printable-gallery.component.html'
})
export class PrintableGalleryComponent extends CommonComponent implements OnInit, OnDestroy {

  @ViewChild('modal') modal!: ElementRef;
  @ViewChild('confirm') confirm!: ElementRef;

  protected readonly faPrint = faPrint;
  protected readonly faRefresh = faRefresh;

  private subscription: Subscription | undefined;
  private httpSubscription: Subscription | undefined;

  protected override limit = 24;
  jobId: any;
  public selectedJobs: any = {};

  private asset: any;
  private page$ = new BehaviorSubject(1);
  private completed: Subject<any> = new Subject();
  private interrupted: Subject<any> = new Subject();

  private page: any = 1;
  private triggered: boolean = false;

  private modalInstance: Modal | undefined;
  private confirmInstance: Modal | undefined;

  public authenticated: boolean = false;

  defaultImage: string = '/assets/images/blank.png';

  public printing: boolean = false;

  constructor(
    private httpClient: HttpClient,
    private notificationService: NotificationService,
    private configService: ConfigService,
    private tracker: TrackerService,
    @Inject('Storage') private storage: Storage
  ) {
    super();
  }

  ngOnInit(): void {

    this.authenticated = this.storage.getItem('authenticated') === 'true';

    if( ! this.authenticated) {
      const pass = prompt('Please enter the password to access this page');
      if(pass === '9987') {
        this.storage.setItem('authenticated', 'true');
        this.authenticated = true;
      }
    }

    this.subscription =  this.configService.config$.pipe(
      tap(config => this.config = config),
      mergeMap(() => this.page$.asObservable()),
      takeUntil(this.completed),
      tap((page) => this.page = page)
    ).subscribe(() => {
      this.startPolling();
    });
  }

  ngOnDestroy(): void {
    if(this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  getQRCode(id: string) {
    const url = window.location.toString().replace('printable-gallery', 'result/' + id + '?qr=1');
    return `https://api.qrserver.com/v1/create-qr-code/?size=600x600&qzone=5&data=${encodeURIComponent(url)}`
  }

  showQrCode(id: string) {
    this.jobId = id;
  }

  hideQrCode() {
    this.jobId = undefined;
  }

  next(id: string, i: number) {
    for (let job of this.jobs) {
      if (job.id === id) {
        this.selectedJobs[id] = {
          current: (i + 1) % this.filter(job.assets).length,
          status: this.filter(job.assets)[(i + 1) % this.filter(job.assets).length].status
        };
      }
    }
  }

  isSelected(id: string, i: number): boolean {

    // console.log(this.selectedJobs);

    if(this.selectedJobs.hasOwnProperty(id)) {
      return this.selectedJobs[id]['current'] === i;
    }
    else {
      return i === 0;
    }

  }

  onScrollingFinished() {
    if ( ! this.triggered) {
      this.page$.next(this.page + 1);
      this.triggered = true;
    }
  }

  onScroll($event: any) {
    // console.log('scroll', $event);
    const target = $event.target as HTMLElement;
    // console.log(target.scrollHeight, target.clientHeight, target.scrollTop);
    if (target.scrollHeight - target.clientHeight - target.scrollTop < 150) {
      this.onScrollingFinished();
    }
    if(target.scrollTop < 1) {
      this.refresh();
    }
  }

  open(id: string) {

    this.jobId = id;

    const options: ModalOptions = {
      placement: 'top-center',
      backdrop: 'dynamic',
      backdropClasses: 'bg-gray-900/50 dark:bg-gray-900/80 fixed inset-0 z-40',
      closable: true
    };

    this.modalInstance = new Modal(this.modal.nativeElement, options);
    this.stopPolling();
    this.modalInstance.show();

    const current = this.modalInstance;

    setTimeout(() => {
      if (current.isVisible()) {
        // console.log('h1');
        current.hide();
      }
    }, 30 * 1000);

    return false;
  }

  dismiss() {
    // console.log('dismiss', this.modalInstance, this.confirmInstance);
    if(this.confirmInstance && this.confirmInstance.isVisible()) {
      this.confirmInstance.hide();

    }
    else if(this.modalInstance && this.modalInstance.isVisible()) {
      // console.log('h2');
      this.modalInstance.hide();
      if(this.canPoll()) {
        this.startPolling();
      }
    }
  }

  print(asset: any) {

    this.asset = asset;

    const options: ModalOptions = {
      placement: 'center',
      backdrop: 'dynamic',
      backdropClasses: 'bg-gray-900/50 dark:bg-gray-900/80 fixed inset-0 z-60',
      closable: true
    };

    this.confirmInstance = new Modal(this.confirm.nativeElement, options);
    this.stopPolling();
    this.confirmPrint();
  }

  confirmPrint() {

    if(this.confirmInstance) {

      let inst = this.confirmInstance;
      inst.show();

      setTimeout(() => {
        inst.hide();
      }, 3*1000);
    }

    if( ! this.asset) {
      return;
    }

    this.printing = true;

    this.tracker.register({
      action: 'print',
      asset: this.asset.id,
      job: this.jobId
    }).then(console.log).catch(console.error);

    setTimeout(() => {
      this.printing = false;
    }, 3 * 1000);

    let bgBlob: any;
    let blob: any;

    this.httpClient.get(this.asset.url, { responseType: 'blob' }).pipe(
      tap((response) => blob = response)
    ).subscribe(() => {

      const subjectImage = new Image();
      subjectImage.onload = () => {
        // console.log('subjectImage size', subjectImage.width, subjectImage.height);

        const tempDiv = document.createElement('div');

        tempDiv.style.position = 'fixed';
        tempDiv.style.top = '-10000px';
        tempDiv.style.left = '-10000px';
        tempDiv.style.width = subjectImage.width + 'px';
        tempDiv.style.height = subjectImage.height + 'px';

        document.body.appendChild(tempDiv);

        const stage = new Konva.Stage({
          container: tempDiv,
          width: subjectImage.width,
          height: subjectImage.height
        });

        const layer = new Konva.Layer();

        const sbImage = new Konva.Image({
          image: subjectImage
        });

        stage.add(layer);
        layer.add(sbImage);
        layer.draw();

        setTimeout(() => {
          stage.toBlob().then(dataBlob => {

            if (dataBlob && dataBlob instanceof Blob) {
              const hiddenFrame = document.createElement("iframe");

              hiddenFrame.onload = () => {
                hiddenFrame.contentWindow!.onbeforeunload = () => {
                  document.body.removeChild(hiddenFrame)
                };
                hiddenFrame.contentWindow!.onafterprint = () => {
                  document.body.removeChild(hiddenFrame)
                };
                hiddenFrame.contentWindow!.focus(); // Required for IE
                hiddenFrame.contentWindow!.print();
              };
              hiddenFrame.style.position = "fixed";
              hiddenFrame.style.right = "-10000px";
              hiddenFrame.style.bottom = "-10000px";
              hiddenFrame.style.width = subjectImage.width + 'px';
              hiddenFrame.style.height = subjectImage.height + 'px';
              hiddenFrame.style.border = "0px";

              hiddenFrame.src = window.URL.createObjectURL(dataBlob);
              document.body.appendChild(hiddenFrame);
              document.body.removeChild(tempDiv);
            }
          });

        }, 1000);
      }

      subjectImage.src = window.URL.createObjectURL(blob);
    });

  }

  refresh() {
    window.location.reload();
  }

  private canPoll() {
    let modals = 0;

    if(this.confirmInstance && this.confirmInstance.isVisible()) {
      modals++;
    }

    if(this.modalInstance && this.modalInstance.isVisible()) {
      modals++;
    }

    return  this.page === 1 && modals === 0;
  }

  private startPolling() {

    if(this.httpSubscription) {
      this.httpSubscription.unsubscribe();
    }

    this.httpSubscription = merge(timer(50), interval(30000))
      .pipe(
        takeUntil(this.interrupted),
        mergeMap(() => this.httpClient.post<Array<{assets: Array<any>, id: string, createdAt: any, status: string}>>(environment.api_v2_url + '/gallery-search?limit=' + this.limit + '&page=' + this.page, { event_ids: [this.config.id, '568eb3fd-03d9-405f-a2d0-4e00a73ec9f1'] })),
        map((items: Array<{ assets: Array<any>, id: string, createdAt: any, status: string }>): Array<{
          assets: Array<any>,
          id: string,
          createdAt: any,
          status: string
        }> => items.map(item => {
          if(typeof item.createdAt === 'string') {
            return { ...item, createdAt: parseJSON(item.createdAt) };
          }
          else {
            return item;
          }
        }))
      )
      .subscribe((items: Array<{assets: Array<any>, id: string, createdAt: Date, status: string}>) => {

        if (items.length === 0) {
          this.completed.next(true);
          return;
        }

        if(this.page > 1) {
          // intersection - all items that are already in the list
          let intersection = this.jobs.filter(job => items.map(i => i.id).includes(job.id));
          for(let item of items) {
            // for every item that is not in the list, add it to the end
            if( ! intersection.map(i => i.id).includes(item.id)) {
              this.jobs.push(item);
            }
          }
        }
        else {
          // first page - just overwrite the list
          this.jobs = items.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
        }

        for (let job of this.jobs) {

          this.selectedJobs[job.id] = {
            current: 0,
            status: this.filter(job.assets)[0].status || 'visible'
          };

        }

        // console.log(this.selectedJobs);
        this.triggered = false;

        if(this.page > 1) {
          this.interrupted.next(true);
        }
      });
  }

  private stopPolling() {
    this.interrupted.next(true);
  }
}
